import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Form, Input, Button, Typography, Alert } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useStore } from '../../hooks/storeContext';
import { ROUTES } from '../../constants/routes';
import {
  LoginContainer,
  LoginCard,
  StyledForm,
  LoginHeader,
  BeatingHeart,
  StyledTitle,
  StyledAlert,
  SignInButton,
  FooterText,
  CopyrightText
} from './styled';

const { Text } = Typography;

const AdminLogin = observer(() => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { authStore } = useStore();

  const handleSubmit = async (values) => {
    setLoading(true);
    setError('');

    try {
      await authStore.onLogin(values);
      
      // Determine where to redirect the user based on their permissions
      let redirectPath = ROUTES.ADMIN_DASHBOARD; // Default for admins
      
      if (authStore.me?.role === 'admin') {
        redirectPath = ROUTES.ADMIN_DASHBOARD;
      } else {
        // For non-admin users, find the first page they have access to
        if (authStore.hasPermission('dashboard')) {
          redirectPath = ROUTES.ADMIN_DASHBOARD;
        } else if (authStore.hasPermission('event_management')) {
          redirectPath = ROUTES.ADMIN_EVENTS;
        } else if (authStore.hasPermission('patient_queue')) {
          redirectPath = ROUTES.ADMIN_PATIENT_QUEUE;
        } else if (authStore.hasPermission('user_management')) {
          redirectPath = ROUTES.ADMIN_USERS;
        } else if (authStore.hasPermission('screening_data')) {
          redirectPath = ROUTES.ADMIN_ALL_MEMBERS;
        } else if (authStore.hasPermission('insurance_enrollment')) {
          redirectPath = ROUTES.ADMIN_INSURANCE_ENROLLMENTS;
        } else {
          // User has no admin permissions, show error
          setError('You do not have access to any admin features. Please contact an administrator.');
          authStore.logout();
          setLoading(false);
          return;
        }
      }
      
      // Return to the admin page that sent them here (e.g. a link in a notification email).
      const from = location.state?.from;
      if (from?.pathname?.startsWith('/admin/')) {
        redirectPath = `${from.pathname}${from.search || ''}`;
      }

      navigate(redirectPath, { replace: true });
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LoginHeader>
          <BeatingHeart />
          <StyledTitle level={2}>
            Admin Portal
          </StyledTitle>
          <Text type="secondary">
            Sign in to access the administration dashboard
          </Text>
        </LoginHeader>

        {error && (
          <StyledAlert>
            <Alert
              message={error}
              type="error"
              showIcon
            />
          </StyledAlert>
        )}

        <StyledForm
          form={form}
          name="admin-login"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Email address"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <SignInButton>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </SignInButton>
          </Form.Item>
        </StyledForm>

        <div style={{ textAlign: 'center', marginTop: -8, marginBottom: 16 }}>
          <Link to={ROUTES.FORGOT_PASSWORD}>Forgot password?</Link>
        </div>

        <FooterText>
          <CopyrightText type="secondary">
            © 2025 Black Heart Association. All rights reserved.
          </CopyrightText>
        </FooterText>
      </LoginCard>
    </LoginContainer>
  );
});

export default AdminLogin;
